import { Checkbox, PrimaryButton, SearchBox, Separator } from '@fluentui/react';
import { ValidationError } from '@v2/components/controls/ValidationError/ValidationError';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { ConstantField, MappedHeader, MappingParams } from '@shared/types/flowstep/mapping';
import { ModalPage } from 'src/components/base/Page';
import { ColumnFieldMappingEditor } from './ColumnFieldMapping/ColumnFieldMappingEditor';
import { ConstantMappings } from './ConstantMappings/ConstantMappings';
import { MappingManagerState } from './mapping-manager-state';
import { useStyles } from './MappingManager.styles';
import { DataField } from 'shared/providers/types';
import { RenderFieldEditorFunc } from 'src/v2/components/controls/editors/types';

interface MappingManagerProps {
  idFieldName?: string;
  objectType: string;
  mappedHeaders: MappedHeader[];
  constants: ConstantField[];
  objectFields: DataField[];
  renderEditorForType: RenderFieldEditorFunc;
  onApplyResult: (result: MappingParams) => void;
}

export const MappingManager: React.FC<MappingManagerProps> = observer((props) => {
  const { idFieldName, objectType, mappedHeaders, constants, objectFields, onApplyResult, renderEditorForType } = props;
  const styles = useStyles();
  const [isConstantsMappingOpen, setConstantsMappingOpen] = React.useState(false);

  const [state] = useState(() => new MappingManagerState());

  useEffect(() => {
    state.initialize(mappedHeaders, objectFields, constants, idFieldName);
  }, [mappedHeaders, constants, objectFields]);

  const handleApply = () => {
    const mappedHeaders = state.buildMappedHeaders(objectFields);

    onApplyResult({
      mappedHeaders,
      objectType,
      constants: state.constantFields,
    });
  }

  const filteredEditorStates = state.filteredEditorStates;

  return (
    <>
      <div className={styles.mappingManager}>
        <div className={styles.constants}>
          <PrimaryButton onClick={() => setConstantsMappingOpen(true)}>
            Constants
          </PrimaryButton>
          <ModalPage
            isOpen={isConstantsMappingOpen}
            onClose={() => setConstantsMappingOpen(false)}
            title='Constants'
            helpLink={undefined}
            component={() => (
              <ConstantMappings
                objectType={objectType}
                objectFields={objectFields}
                constantFields={state.constantFields}
                mappingFields={state.editorStates}
                renderEditorForType={renderEditorForType}
                onApplyResult={(result) => {
                  state.constantFields = result;
                  setConstantsMappingOpen(false);
                }}
              />
            )}
          />
        </div>
        <Separator className={styles.separator} />
        <div className={styles.searchBox}>
          <Checkbox
            onChange={(_, checked) => state.setAllCheckbox(!!checked)}
            checked={state.hasAllColumnsChecked}
          />
          <SearchBox
            iconProps={{ iconName: 'Filter' }}
            placeholder="Filter columns by name"
            className="input"
            onChange={(_, value) => state.filterString = value || ''}
          />
        </div>
        <Separator className={styles.separator} />
        <ValidationError message={state.validationErrors.get('id-field-error')} />
        {filteredEditorStates.map((editorState) => (
          <ColumnFieldMappingEditor
            key={editorState.columnName}
            state={editorState}
            objectFields={objectFields}
            errorMessage={state.validationErrors.get(editorState.columnName)}
          />
        ))}
      </div>
      <div className={styles.fixedBottomPanel}>
        <PrimaryButton
          onClick={handleApply}
          disabled={state.validationErrors.size > 0}
        >
          Apply
        </PrimaryButton>
      </div>
    </>
  );
});
